import React, { useEffect, useMemo, useRef } from "react";

import EmptyChart from "../../../../../../../../components/EmptyChart";
import Spinner from "../../../../../../../../components/Spinner";

import { ChartOptions } from "../../../../../../schemas/report";

// hooks
import useStore from "../../../../store";
import { ChartType as PYChart } from "../../../../store/enums";

import { useGetGridDataCount, useGetRawReviewReportData } from "../../../../../../api/hooks";

import BarChart from "../ChartHooks/BarChart/BarChart";
import useChartOptions from "../ChartHooks/UseChartOptions/useChartOptions";
import { useDownloadImage } from "../ChartHooks/UseDownloadImage/useDownloadImage";
import { ChartContainer, VisualizationHeader } from "../Layout";
import css from "../Layout/Layout.module.scss";
import OptionSettings from "../shared/OptionSettings";

const { xAxisRangeOptions } = OptionSettings[PYChart.ColumnChart];

const ReportingColumnChart = () => {
	const chartRef = useRef(null);

	const [getAppliedFilters, report, canViewSummary] = useStore((state) => [
		state.getAppliedFilters,
		state.report,
		state.canViewSummary,
	]);

	const {
		summarizeBySetting,
		visualizeOption,
		aggregationOperations,
		sortSettings,
		selectableSortSettings,
		dataSeriesOptions,
		previousChart,
		previousVisualizeOption,
		getChartDataFromRawData,
		anyAxisOptions,
		setVisualizeOptions,
		getAxisOptionsAsColumns,
	} = useChartOptions(PYChart.ColumnChart);

	const { downloadImage } = useDownloadImage(chartRef, {
		chartType: PYChart.ColumnChart,
		xAxisLabel: "Metric",
	});

	const yAxisOptions = anyAxisOptions;

	useEffect(() => {
		handleSetDefaultColumnChartOptions();
	}, []);

	useEffect(() => {
		if (!anyAxisOptions?.some((option) => option.value === visualizeOption?.yAxis?.column)) {
			if (visualizeOption?.yAxis?.column) {
				handleSetDefaultColumnChartOptions(true);
			}
		}
	}, [anyAxisOptions?.length]);

	const variables = {
		questionGroupingSessionID: report?.questionGroupingSessionID,
		columns: getAxisOptionsAsColumns(),
		filters: getAppliedFilters(),
		pageNumber: "1",
		pageSize: "25",
		sorts: sortSettings,
		summarizeBy: summarizeBySetting,
		aggregationOperations,
	};

	const prefetchVariables: (typeof variables)[] = [];

	for (let i = 0; i < selectableSortSettings.length; i++) {
		if (
			JSON.stringify(selectableSortSettings[i]) !== JSON.stringify(sortSettings) &&
			(i === 0 || JSON.stringify(selectableSortSettings[i]) !== JSON.stringify(selectableSortSettings[i - 1]))
		) {
			prefetchVariables.push({
				...variables,
				sorts: selectableSortSettings[i],
			});
		}
	}

	const handleSetDefaultColumnChartOptions = (forceUpdate = false) => {
		const options: ChartOptions = {};

		if (dataSeriesOptions?.length > 0) {
			const hasRangeOptionCarryover = xAxisRangeOptions.some(
				(option) => option.value === previousVisualizeOption?.dataSeries?.rangeOption
			);
			options.dataSeries = {
				column:
					previousVisualizeOption?.dataSeries?.column ||
					visualizeOption?.dataSeries?.column ||
					dataSeriesOptions[dataSeriesOptions.length - 1].value,
				rangeOption: hasRangeOptionCarryover
					? previousVisualizeOption?.dataSeries?.rangeOption
					: visualizeOption?.dataSeries?.rangeOption || xAxisRangeOptions[1].value,
			};
		}

		if (yAxisOptions?.length > 0) {
			options.yAxis = {
				column:
					previousVisualizeOption?.yAxis?.column ||
					previousVisualizeOption?.xAxis?.column ||
					yAxisOptions[0].value,
			};
			if (forceUpdate) {
				options.yAxis.column = yAxisOptions[0].value;
			}
		}

		if (Object.keys(visualizeOption).length === 0 || forceUpdate) {
			const newVisualizeOptions = { [PYChart.ColumnChart]: { ...visualizeOption, ...options } };
			if (previousChart !== PYChart.ColumnChart) {
				newVisualizeOptions[previousChart] = null;
			}
			setVisualizeOptions(newVisualizeOptions);
			return;
		}

		// Current behavior: when there is visualization option on first render, if the selected dataseries is columns doesn't exist, default to the first options
		if (Object.keys(visualizeOption).length > 0) {
			const hasDataSeriesColumn = dataSeriesOptions.some(
				(option) => option.value === visualizeOption.dataSeries?.column
			);
			const hasYAxisColumn = yAxisOptions.some((option) => option.value === visualizeOption.yAxis?.column);

			if (!hasDataSeriesColumn) {
				options.dataSeries = {
					column: dataSeriesOptions[dataSeriesOptions.length - 1].value,
					rangeOption: xAxisRangeOptions[1].value,
				};
			}

			if (!hasYAxisColumn) {
				options.yAxis = {
					column: anyAxisOptions[0].value,
				};
			}

			const newVisualizeOptions = { [PYChart.ColumnChart]: { ...visualizeOption, ...options } };
			setVisualizeOptions(newVisualizeOptions);
		}
	};

	const { data: rawData, isLoading: isRawDataLoading } = useGetRawReviewReportData(
		[variables, ...prefetchVariables],
		canViewSummary,
		report?.type
	)[0];
	const { data: countData } = useGetGridDataCount(variables, report?.type);
	const { labels, datasets = [] } = useMemo(
		() => getChartDataFromRawData(rawData),
		[JSON.stringify(rawData), JSON.stringify(visualizeOption)]
	);

	const emptyDatasets = datasets.filter((item) => item !== null).length === 0;

	// Check if selected column is count aggregation to render the tooltip accordingly
	const selectedCountAggregation =
		anyAxisOptions &&
		anyAxisOptions
			.find((option) => option.value === visualizeOption?.yAxis?.column)
			?.labelWithAggregation.indexOf("(Count)") !== -1;

	return (
		<ChartContainer padding="16px 0px 16px 0px" width="calc(100% - 4px)" testId="columnChart">
			{isRawDataLoading && (
				<div className={css.spinner}>
					<Spinner />
				</div>
			)}
			{!isRawDataLoading && emptyDatasets && canViewSummary && <EmptyChart type={PYChart.ColumnChart} />}
			{!isRawDataLoading && !emptyDatasets && (
				<>
					<VisualizationHeader
						viewingCount={datasets.length}
						totalCount={countData?.reviewReportsCount?.count || 0}
						options={[
							{
								label: "Download Image",
								action: downloadImage,
							},
						]}
					/>
					<BarChart
						ref={chartRef}
						labels={labels}
						datasets={datasets}
						yLabelText={
							anyAxisOptions &&
							anyAxisOptions.find((option) => option.value === visualizeOption?.yAxis?.column)
								?.labelWithAggregation
						}
						isCountAggregation={selectedCountAggregation}
					/>
				</>
			)}
		</ChartContainer>
	);
};

export default ReportingColumnChart;
